import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Table,
  Switch,
  Input,
  Tag,
  Space,
  Button,
  message,
  Spin,
  Alert,
  Collapse,
  Typography,
  Select,
  Popover,
  Tooltip,
} from "antd";
import { SearchOutlined, ReloadOutlined, EditOutlined } from "@ant-design/icons";
import { api } from "../../api/api";
import { useFeatureVisibility } from "../../contexts/featureVisibility.context";
import { useAuth } from "../../contexts/auth.context";
import { hasAdminAccess } from "../../utils/roleUtils";
import { UserRole } from "../../types/user.type";

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface Feature {
  _id: string;
  featureKey: string;
  category: string;
  label: string;
  description?: string;
  enabled: boolean;
  roles: string[];
  route?: string;
  order: number;
}

const categoryLabels: { [key: string]: string } = {
  system: "Системные",
  production: "Производство",
  clients: "Клиенты и заказы",
  finance: "Финансы",
  reports: "Отчеты",
  warehouse: "Склад и инвентарь",
  staff: "Персонал",
  crm: "CRM и продажи",
  documentation: "Документация",
  utilities: "Служебные",
};

export default function FeatureVisibility() {
  const { authState } = useAuth();
  const { refresh: refreshFeatures } = useFeatureVisibility();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const isAdmin = hasAdminAccess(authState.user?.role);

  // Все доступные роли
  const allRoles = Object.values(UserRole);

  useEffect(() => {
    loadFeatures();
  }, []);

  const loadFeatures = async () => {
    try {
      setLoading(true);
      const response = await api.get("/settings/features");
      setFeatures(response.data.features || []);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке разделов");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeature = async (featureKey: string, enabled: boolean) => {
    try {
      setSaving(featureKey);
      await api.put(`/settings/features/${featureKey}`, { enabled });
      
      setFeatures((prev) =>
        prev.map((f) => (f.featureKey === featureKey ? { ...f, enabled } : f))
      );
      
      // Обновляем контекст видимости фич
      await refreshFeatures();
      
      message.success(
        enabled ? "Раздел включен" : "Раздел выключен"
      );
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при обновлении раздела");
    } finally {
      setSaving(null);
    }
  };

  const handleBulkToggle = async (category: string, enabled: boolean) => {
    try {
      const categoryFeatures = filteredFeatures.filter((f) => f.category === category);
      const updates = categoryFeatures.map((f) => ({
        featureKey: f.featureKey,
        enabled,
      }));

      setSaving(`category-${category}`);
      await api.post("/settings/features/bulk", { features: updates });

      setFeatures((prev) =>
        prev.map((f) =>
          categoryFeatures.some((cf) => cf.featureKey === f.featureKey)
            ? { ...f, enabled }
            : f
        )
      );

      await refreshFeatures();
      message.success(
        `${enabled ? "Включены" : "Выключены"} все разделы категории "${categoryLabels[category] || category}"`
      );
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при массовом обновлении");
    } finally {
      setSaving(null);
    }
  };

  const handleUpdateRoles = async (featureKey: string, roles: string[]) => {
    try {
      await api.put(`/settings/features/${featureKey}/roles`, { roles });
      
      setFeatures((prev) =>
        prev.map((f) => (f.featureKey === featureKey ? { ...f, roles } : f))
      );
      
      await refreshFeatures();
      message.success("Роли раздела обновлены");
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при обновлении ролей");
      throw error; // Пробрасываем ошибку, чтобы RolesEditor мог обработать
    }
  };

  const filteredFeatures = useMemo(() => {
    if (!searchText) return features;

    const searchLower = searchText.toLowerCase();
    return features.filter(
      (f) =>
        f.label.toLowerCase().includes(searchLower) ||
        f.featureKey.toLowerCase().includes(searchLower) ||
        f.category.toLowerCase().includes(searchLower) ||
        f.description?.toLowerCase().includes(searchLower)
    );
  }, [features, searchText]);

  const groupedFeatures = useMemo(() => {
    const groups: { [key: string]: Feature[] } = {};
    filteredFeatures.forEach((feature) => {
      if (!groups[feature.category]) {
        groups[feature.category] = [];
      }
      groups[feature.category].push(feature);
    });

    // Сортируем фичи внутри категорий по order
    Object.keys(groups).forEach((category) => {
      groups[category].sort((a, b) => a.order - b.order);
    });

    return groups;
  }, [filteredFeatures]);

  // Компонент для редактирования ролей
  const RolesEditor = ({ feature }: { feature: Feature }) => {
    const [editingRoles, setEditingRolesLocal] = useState<string[]>(feature.roles);
    const [isEditing, setIsEditing] = useState(false);
    const [savingRoles, setSavingRoles] = useState(false);

    const handleSave = async () => {
      setSavingRoles(true);
      try {
        await handleUpdateRoles(feature.featureKey, editingRoles);
        setIsEditing(false);
      } finally {
        setSavingRoles(false);
      }
    };

    const handleCancel = () => {
      setEditingRolesLocal(feature.roles);
      setIsEditing(false);
    };

    if (!isAdmin) {
      return (
        <Space wrap>
          {feature.roles.length > 0 ? (
            feature.roles.map((role) => (
              <Tag key={role} color="purple" style={{ fontSize: 11 }}>
                {role}
              </Tag>
            ))
          ) : (
            <Tag color="default" style={{ fontSize: 11 }}>
              Нет ролей
            </Tag>
          )}
        </Space>
      );
    }

    if (isEditing) {
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Select
            mode="multiple"
            value={editingRoles}
            onChange={setEditingRolesLocal}
            options={allRoles.map((role) => ({ label: role, value: role }))}
            style={{ width: "100%", minWidth: 200 }}
            placeholder="Выберите роли"
            maxTagCount="responsive"
          />
          <Space>
            <Button
              size="small"
              type="primary"
              onClick={handleSave}
              loading={savingRoles}
            >
              Сохранить
            </Button>
            <Button size="small" onClick={handleCancel}>
              Отмена
            </Button>
          </Space>
        </Space>
      );
    }

    return (
      <Space wrap>
        {feature.roles.length > 0 ? (
          feature.roles.map((role) => (
            <Tag key={role} color="purple" style={{ fontSize: 11 }}>
              {role}
            </Tag>
          ))
        ) : (
          <Tag color="default" style={{ fontSize: 11 }}>
            Нет ролей
          </Tag>
        )}
        <Tooltip title="Редактировать роли">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            style={{ padding: 0, height: "auto" }}
          />
        </Tooltip>
      </Space>
    );
  };

  const columns = [
    {
      title: "Раздел",
      dataIndex: "label",
      key: "label",
      render: (text: string, record: Feature) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          {record.description && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.description}
            </Text>
          )}
          <div style={{ marginTop: 4 }}>
            <Tag color="blue" style={{ fontSize: 11 }}>
              {record.featureKey}
            </Tag>
            {record.route && (
              <Tag color="default" style={{ fontSize: 11 }}>
                {record.route}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Роли",
      dataIndex: "roles",
      key: "roles",
      width: 350,
      render: (_: string[], record: Feature) => <RolesEditor feature={record} />,
    },
    {
      title: "Статус",
      dataIndex: "enabled",
      key: "enabled",
      width: 120,
      render: (enabled: boolean, record: Feature) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggleFeature(record.featureKey, checked)}
          loading={saving === record.featureKey}
          checkedChildren="Вкл"
          unCheckedChildren="Выкл"
          disabled={!isAdmin}
        />
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <Alert
        message="Доступ запрещен"
        description="Только администраторы могут управлять видимостью разделов."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={3} style={{ margin: 0 }}>
              Управление видимостью разделов
            </Title>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadFeatures}
              loading={loading}
            >
              Обновить
            </Button>
          </div>
        }
        extra={
          <Input
            placeholder="Поиск по названию, ключу или категории..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Управляйте видимостью разделов приложения. Отключенные разделы будут скрыты из меню
            и недоступны для всех пользователей, кроме администраторов.
          </Text>
        </div>

        {loading ? (
          <Spin tip="Загрузка разделов..." />
        ) : (
          <Collapse
            defaultActiveKey={Object.keys(groupedFeatures)}
            style={{ background: "transparent" }}
          >
            {Object.entries(groupedFeatures)
              .sort(([a], [b]) => {
                const orderA = features.find((f) => f.category === a)?.order || 0;
                const orderB = features.find((f) => f.category === b)?.order || 0;
                return orderA - orderB;
              })
              .map(([category, categoryFeatures]) => {
                const enabledCount = categoryFeatures.filter((f) => f.enabled).length;
                const totalCount = categoryFeatures.length;
                const allEnabled = enabledCount === totalCount;
                const allDisabled = enabledCount === 0;

                return (
                  <Panel
                    key={category}
                    header={
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                          {categoryLabels[category] || category} ({enabledCount}/{totalCount})
                        </span>
                        <Space>
                          {!allEnabled && (
                            <Button
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBulkToggle(category, true);
                              }}
                              loading={saving === `category-${category}`}
                            >
                              Включить все
                            </Button>
                          )}
                          {!allDisabled && (
                            <Button
                              size="small"
                              danger
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBulkToggle(category, false);
                              }}
                              loading={saving === `category-${category}`}
                            >
                              Выключить все
                            </Button>
                          )}
                        </Space>
                      </div>
                    }
                  >
                    <Table
                      dataSource={categoryFeatures}
                      columns={columns}
                      rowKey="featureKey"
                      pagination={false}
                      size="small"
                    />
                  </Panel>
                );
              })}
          </Collapse>
        )}

        {filteredFeatures.length === 0 && !loading && (
          <Alert
            message="Разделы не найдены"
            description="Попробуйте изменить поисковый запрос."
            type="info"
            showIcon
          />
        )}
      </Card>
    </div>
  );
}
